import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { printInvoiceHTML } from "../../utils/invoicePrinter";

function formatMoney(amount, currency = "AUD") {
  const n = Number(amount || 0);
  try {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: String(currency || "AUD").toUpperCase(),
    }).format(n);
  } catch {
    return `${n.toFixed(2)} ${String(currency || "AUD").toUpperCase()}`;
  }
}

/** DD/MM/YYYY, hh:mm AM/PM */
function formatDateTimeAU(dt) {
  if (!dt) return "—";
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return String(dt);

  const date = d.toLocaleDateString("en-AU"); // dd/mm/yyyy
  const time = d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
  return `${date}, ${time}`;
}

/**
 * Best-effort: infer subscription start/end from the order.
 * Uses these fields if present:
 *  - subscription_start / start_at / period_start / current_period_start
 *  - subscription_end / expires_at / period_end / current_period_end
 * Fallback:
 *  - start = created_at
 *  - end = created_at + plan duration (monthly/yearly/lifetime)
 */
function getStartEnd(order) {
  const pick = (...keys) => {
    for (const k of keys) {
      const v = order?.[k];
      if (v) return v;
    }
    return null;
  };

  const start =
    pick("subscription_start", "start_at", "period_start", "current_period_start", "starts_at") ||
    order?.created_at;

  const end =
    pick(
      "subscription_end",
      "expires_at",
      "period_end",
      "current_period_end",
      "ends_at",
      "expiry_at"
    ) || null;

  // If backend didn't provide end, infer from plan
  if (!end && start) {
    const d = new Date(start);
    if (!Number.isNaN(d.getTime())) {
      const plan = String(order?.plan || "").toLowerCase();
      if (plan.includes("monthly")) d.setMonth(d.getMonth() + 1);
      else if (plan.includes("yearly") || plan.includes("annual")) d.setFullYear(d.getFullYear() + 1);
      else if (plan.includes("lifetime")) return { start, end: "Never" };
      return { start, end: d.toISOString() };
    }
  }

  return { start, end };
}

function Modal({ open, title, onClose, children, footer }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="accModalBackdrop" onMouseDown={onClose}>
      <div className="accModal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="accModalHeader">
          <div className="accModalTitle">{title}</div>
          <button className="accModalClose" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="accModalBody">{children}</div>
        {footer ? <div className="accModalFooter">{footer}</div> : null}
      </div>
    </div>
  );
}

function InvoiceView({ order, user }) {
  const invoiceNumber = useMemo(() => {
    const id = order?.id ?? "";
    return `INV-${String(id).padStart(6, "0")}`;
  }, [order?.id]);

  const planLabel = String(order?.plan || "").toUpperCase() || "SUBSCRIPTION";
  const currency = (order?.currency || "AUD").toUpperCase();

  const total =
    order?.total_amount != null
      ? Number(order.total_amount)
      : Number(order?.amount_cents || 0) / 100;

  const created = order?.created_at ? formatDateTimeAU(order.created_at) : "—";

  return (
    <div className="accInvoiceWrap" id="invoice-print-area">
      <div className="accInvoiceTop">
        <div>
          <div className="accInvoiceBrand">MyChineseHoroscope</div>
          <div className="accInvoiceMeta">
            <div>
              Invoice: <b>{invoiceNumber}</b>
            </div>
            <div>
              Date: <b>{created}</b>
            </div>
            <div>
              Status: <b>{order?.status || "paid"}</b>
            </div>
            {order?.stripe_invoice_id ? (
              <div>
                Stripe invoice: <b>{order.stripe_invoice_id}</b>
              </div>
            ) : null}
            {order?.stripe_session_id ? (
              <div>
                Stripe session: <b>{order.stripe_session_id}</b>
              </div>
            ) : null}
          </div>
        </div>

        <div className="accInvoiceTo">
          <div className="accInvoiceToTitle">Billed To</div>
          <div className="accInvoiceToLine">
            <b>{user?.name || ""}</b>
          </div>
          <div className="accInvoiceToLine">{user?.email || ""}</div>
        </div>
      </div>

      <div className="accInvoiceTable">
        <div className="accInvoiceRowHead">
          <div>Description</div>
          <div className="accRight">Amount</div>
        </div>

        <div className="accInvoiceRow">
          <div>
            <div className="accItemTitle">{planLabel} subscription</div>
            <div className="accItemSub">Access to premium horoscope features</div>
          </div>
          <div className="accRight accMoney">{formatMoney(total, currency)}</div>
        </div>

        <div className="accInvoiceTotals">
          <div className="accTotalLine">
            <span>Subtotal</span>
            <span className="accMoney">{formatMoney(total, currency)}</span>
          </div>
          <div className="accTotalLine">
            <span>Tax</span>
            <span className="accMoney">{formatMoney(0, currency)}</span>
          </div>
          <div className="accTotalLineGrand">
            <span>Total</span>
            <span className="accMoney">{formatMoney(total, currency)}</span>
          </div>
        </div>
      </div>

      <div className="accInvoiceFooter">
        <div>Thanks for your purchase.</div>
        <div className="accSmall">If you need support, contact support@mychinesehoroscope.com</div>
      </div>
    </div>
  );
}

export default function AccountOrders({ user }) {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setErr("");
        const r = await api.get("payments/my-orders");
        setRows(r?.data?.rows || []);
      } catch (e) {
        setErr(e?.response?.data?.message || e.message || "Failed to load orders");
      }
    })();
  }, []);

  const downloadInvoice = () => {
    const el = document.getElementById("invoice-print-area");
    if (!el) return;

    // IMPORTANT:
    // This is "library-free PDF": we open print dialog with a clean HTML doc + logo.
    // User selects "Save as PDF".
    printInvoiceHTML({
      html: el.outerHTML,
      title: selected ? `Invoice Order #${selected.id}` : "Invoice",
      logoUrl: "/my-chinese-horoscope-logo.png",
    });
  };

  return (
    <div className="accPage">
      <div className="accPageTitle">Order history</div>

      {err ? <div className="accAlert accAlertError">{err}</div> : null}

      <div className="accOrderList">
        {rows?.length ? (
          rows.map((o) => {
            const total =
              o?.total_amount != null
                ? Number(o.total_amount)
                : Number(o?.amount_cents || 0) / 100;

            const currency = (o?.currency || "AUD").toUpperCase();
            const { start, end } = getStartEnd(o);

            const startText = start ? formatDateTimeAU(start) : "—";
            const endText = end === "Never" ? "Never" : end ? formatDateTimeAU(end) : "—";

            return (
              <button
                key={o.id}
                className="accOrderCard"
                onClick={() => setSelected(o)}
                type="button"
              >
                <div className="accOrderTop">
                  <div className="accOrderPlan" style={{ color: "#000" }}>
                    {(o.plan || "subscription").toUpperCase()} — {formatMoney(total, currency)}
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <div className="accOrderId" style={{ color: "#000" }}>
                      #{o.id}
                    </div>

                    {/* status moved under id */}
                    <div style={{ marginTop: 6 }}>
                      <span className="accBadge" style={{ color: "#000" }}>
                        {String(o.status || "paid").toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="accOrderMeta" style={{ color: "#000" }}>
                  <span>
                    <b>Subscription start:</b> {startText}
                  </span>
                  <span className="accDot">•</span>
                  <span>
                    <b>Subscription expire:</b> {endText}
                  </span>
                </div>

                <div className="accOrderHint" style={{ color: "#000" }}>
                  Click to view invoice
                </div>
              </button>
            );
          })
        ) : (
          <div className="accEmpty">No orders yet.</div>
        )}
      </div>

      <Modal
        open={!!selected}
        title={selected ? `Invoice for Order #${selected.id}` : "Invoice"}
        onClose={() => setSelected(null)}
        footer={
          <div className="accModalActions">
            <button className="accBtn accBtnSecondary" onClick={() => setSelected(null)}>
              Close
            </button>

            {/* ✅ Print removed; now "Download invoice" */}
            <button className="accBtn accBtnPrimary" onClick={downloadInvoice}>
              Print Invoice
            </button>
          </div>
        }
      >
        {selected ? <InvoiceView order={selected} user={user} /> : null}
      </Modal>
    </div>
  );
}
